import { Box } from "@chakra-ui/react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $isRangeSelection,
  COPY_COMMAND,
  CUT_COMMAND,
  DELETE_CHARACTER_COMMAND,
  PASTE_COMMAND,
} from "lexical";
import * as React from "react";
import { useMemo } from "react";
import {
  RiClipboardLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiScissorsCutLine,
} from "react-icons/ri";
import { SHORTCUTS } from "../ShortcutsPlugin/shortcuts";
import { ContextMenuOption, ContextMenuPlugin } from "./ContextMenuPlugin";

const ContextMenuIcon = ({ icon }: { icon: React.ReactElement }) => (
  <Box width="20px">{icon}</Box>
);

const ContextMenuNoIcon = () => <Box width="20px" />;

export default function DefaultContextMenuPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();

  const defaultOptions = useMemo((): ContextMenuOption[] => {
    return [
      {
        type: "item",
        title: "Copy",
        shortcut: SHORTCUTS.COPY,
        icon: <ContextMenuIcon icon={<RiFileCopyLine />} />,
        $onSelect: () => {
          editor.dispatchCommand(COPY_COMMAND, null);
        },
        $disableOn: ({ selection }) => !selection || selection.isCollapsed(),
      },
      {
        type: "item",
        title: "Cut",
        shortcut: SHORTCUTS.CUT,
        icon: <ContextMenuIcon icon={<RiScissorsCutLine />} />,
        $onSelect: () => {
          editor.dispatchCommand(CUT_COMMAND, null);
        },
        $disableOn: ({ selection }) => !selection || selection.isCollapsed(),
      },
      {
        type: "item",
        title: "Paste",
        shortcut: SHORTCUTS.PASTE,
        icon: <ContextMenuIcon icon={<RiClipboardLine />} />,
        $onSelect: () => {
          navigator.clipboard.read().then(async function () {
            const permission = await navigator.permissions.query({
              // @ts-expect-error These types are incorrect.
              name: "clipboard-read",
            });
            if (permission.state === "denied") {
              alert("Not allowed to paste from clipboard.");
              return;
            }

            const data = new DataTransfer();
            const items = await navigator.clipboard.read();
            const item = items[0];

            if (item) {
              for (const type of item.types) {
                const dataString = await (await item.getType(type)).text();
                data.setData(type, dataString);
              }
            }

            const event = new ClipboardEvent("paste", {
              clipboardData: data,
            });

            editor.dispatchCommand(PASTE_COMMAND, event);
          });
        },
      },
      {
        type: "item",
        title: "Paste as Plain Text",
        shortcut: SHORTCUTS.PASTE_WITHOUT_FORMATTING,
        icon: <ContextMenuNoIcon />,
        $onSelect: () => {
          navigator.clipboard.readText().then(async function (plainText) {
            const permission = await navigator.permissions.query({
              // @ts-expect-error These types are incorrect.
              name: "clipboard-read",
            });
            if (permission.state === "denied") {
              alert("Not allowed to paste from clipboard.");
              return;
            }

            const data = new DataTransfer();
            data.setData("text/plain", plainText);

            const event = new ClipboardEvent("paste", {
              clipboardData: data,
            });
            editor.dispatchCommand(PASTE_COMMAND, event);
          });
        },
      },
      { type: "divider" },
      {
        type: "item",
        title: "Delete",
        shortcut: null,
        icon: <ContextMenuIcon icon={<RiDeleteBinLine />} />,
        $disableOn: ({ selection }) =>
          !selection ||
          !$isRangeSelection(selection) ||
          selection.isCollapsed(),
        $onSelect: () => {
          editor.dispatchCommand(DELETE_CHARACTER_COMMAND, false);
        },
      },
    ];
  }, [editor]);

  return <ContextMenuPlugin items={defaultOptions} />;
}
